import { Component, inject, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component';
import { PageSizeComponent } from '../../../../shared/components/page-size/page-size.component';
import { PaginationComponent } from '../../../../shared/components/pagination/pagination.component';
import { FormsModule } from '@angular/forms';
import { PopupComponent } from '../../../../shared/components/popup/popup.component';
import { OutreachLinkCreateComponent } from '../outreach-link-create/outreach-link-create.component';
import { OutreachLinkDetailsComponent } from '../outreach-link-details/outreach-link-details.component';
import { OutreachLink } from '../../models/outreach-link';
import { takeUntil } from 'rxjs';
import { PublicLinkService } from '../../services/public-link.service';
import { FilterComponent } from '../../../../shared/components/filter/filter.component';
import { SearchInputComponent } from '../../../../shared/components/search-input/search-input.component';

@Component({
  selector: 'app-outreach-links',
  standalone: true,
  imports: [
    SvgIconComponent,
    PageSizeComponent,
    PaginationComponent,
    FormsModule,
    PopupComponent,
    DatePipe,
    OutreachLinkCreateComponent,
    OutreachLinkDetailsComponent,
    FilterComponent,
    SearchInputComponent,
  ],
  templateUrl: './outreach-links.component.html',
  styleUrl: './outreach-links.component.scss',
})
export class OutreachLinksComponent
  extends MenuComponentBase
  implements OnInit
{
  publicLinkService = inject(PublicLinkService);

  showCreatePopup = false;
  showDeleteConfirmPopup = false;
  showDetailsPopup = false;
  loading = true;
  outreachLinks: OutreachLink[] = [];
  currentPage = 1;
  pageSize = 50;
  totalLinks = 0;
  searchQuery = '';
  deletePopupLink!: OutreachLink;
  selectedLinkId!: number;
  filterBy = 'all';
  filterByOptions = [
    { option: 'All', value: 'all' },
    { option: 'ACTIVE', value: 'True' },
    { option: 'INACTIVE', value: 'False' },
  ];

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadQRCodes();
  }

  loadQRCodes(): void {
    this.publicLinkService
      .getAllPublicOutreachLinks(
        this.currentPage,
        this.pageSize,
        this.searchQuery,
        this.filterBy,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.outreachLinks = res.results;
          this.totalLinks = res.count;
          this.loading = false;
        },
        error: (error) => {
          this.loading = false;
          console.error('Error loading outreach links:', error);
        },
      });
  }

  onClosePopup(isClickedOutside: boolean): void {
    if (isClickedOutside) return;
    this.showCreatePopup = false;
  }

  onLinkGenerated(linkId: number): void {
    this.showCreatePopup = false;
    this.selectedLinkId = linkId;
    this.showDetailsPopup = true;
    this.loadQRCodes(); // Refresh the list to show the new link
  }

  onDeleteLink(qrCode: OutreachLink): void {
    this.deletePopupLink = qrCode;
    this.showDeleteConfirmPopup = true;
  }

  onViewDetails(link: OutreachLink): void {
    this.selectedLinkId = link.id;
    this.showDetailsPopup = true;
  }

  onDetailsPopupClosed(): void {
    this.showDetailsPopup = false;
  }

  onConfirmDelete(): void {
    if (!this.deletePopupLink) return;
    this.publicLinkService
      .deleteOutreachLink(this.deletePopupLink.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.outreachLinks = this.outreachLinks.filter(
            (link) => link.id !== this.deletePopupLink!.id,
          );
          this.totalLinks--;
          this.showDeleteConfirmPopup = false;
        },
        error: (error) => {
          console.error('Error deleting outreach link:', error);
          this.showDeleteConfirmPopup = false;
        },
      });
  }

  onLinkUpdated(link: OutreachLink): void {
    const index = this.outreachLinks.findIndex((l) => l.id === link.id);
    if (index !== -1) {
      this.outreachLinks[index] = link;
    } else {
      this.outreachLinks.push(link);
    }
  }

  onLinkStatusChange(linkId: number): void {
    const link = this.outreachLinks.find((l) => l.id === linkId);
    if (link) {
      const newStatus = !link.is_active;
      this.outreachLinks = this.outreachLinks.map((l) =>
        l.id === linkId ? { ...l, is_active: newStatus } : l,
      );
    }
  }

  onLinkDeleted(linkId: number): void {
    this.outreachLinks = this.outreachLinks.filter(
      (link) => link.id !== linkId,
    );
    this.totalLinks--;
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadQRCodes();
  }

  onPageSizeChange(newPageSize: number): void {
    if (newPageSize !== this.pageSize) {
      this.pageSize = newPageSize;
      this.currentPage = 1;
      this.loadQRCodes();
    }
  }

  onFilterByChange(value: string): void {
    this.filterBy = value;
    this.currentPage = 1;
    this.loadQRCodes();
  }

  onSearchQueryChange(query: string): void {
    this.searchQuery = query;
    this.currentPage = 1;
    // In real app, call API with search query
    this.loadQRCodes();
  }
}
